// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import apiClient from "../config/api";
import { handleApiCall, buildParams } from "../utils/apiHelper";

export const availabilityService = {
  /**
   * Get availability with pagination
   */
  getAvailability: async (skip = 0, limit = 25) => {
    const params = buildParams({ skip, limit });

    return handleApiCall(
      () => apiClient.get(`availability/block`, { params }),
      "Error fetching availability",
    );
  },

  /**
   * Create single availability block
   */
  createAvailability: async (availabilityData) => {
    return handleApiCall(
      () => apiClient.post(`availability/block`, availabilityData),
      "Error creating availability",
    );
  },

  /**
   * Update availability block
   */
  updateAvailability: async (availabilityId, availabilityData) => {
    return handleApiCall(
      () =>
        apiClient.put(`availability/block/${availabilityId}`, availabilityData),
      "Error updating availability",
    );
  },

  /**
   * Delete availability block
   */
  deleteAvailability: async (availabilityId) => {
    return handleApiCall(
      () => apiClient.delete(`availability/block/${availabilityId}`),
      "Error deleting availability",
    );
  },

  /**
   * Get availability scenarios
   */
  getAvailabilityScenarios: async () => {
    return handleApiCall(
      () => apiClient.get(`availability/scenarios`),
      "Error fetching availability scenarios",
    );
  },

  /**
   * Unified occupied-days feed for the AdminView Availability View.
   * Merges confirmed VHP bookings (source = "booking") with SEAS-detected
   * infrastructure events (source = "seas") into a single day-level list.
   *
   * GET /availability/occupied
   *   location_id  (required) – filter by location
   *   scenario_id  (optional) – omit for all scenarios
   *   date_from    (required) – ISO 8601 start of range
   *   date_to      (required) – ISO 8601 end of range
   *   source       (optional) – "booking" | "seas" | "all" (default)
   *
   * @param {Object} params
   * @param {Object} [options] - { signal } to support request cancellation
   */
  getOccupied: async (
    { locationId, scenarioId, dateFrom, dateTo, source, tenantId } = {},
    options = {},
  ) => {
    const params = buildParams({
      location_id: locationId,
      scenario_id: scenarioId && scenarioId !== "all" ? scenarioId : undefined,
      date_from: dateFrom,
      date_to: dateTo,
      source: source && source !== "all" ? source : undefined,
      // Super-admin only; backend ignores it for other roles.
      tenant_id: tenantId && tenantId !== "all" ? tenantId : undefined,
    });

    return handleApiCall(
      () => apiClient.get(`availability/occupied`, { params, ...options }),
      "Error fetching occupied days",
    );
  },
};
